import { Button, Modal, Progress, Tag } from "antd";
import {
  getTaskPreviewArtifact,
  getTaskProgressStatus,
  getTaskPrompt,
  getTaskStatusColor,
  isTerminalTaskStatus,
  taskCapabilityLabels,
  taskStatusLabels,
} from "../model/task-display";
import type { GenerationTask } from "../model/generation-task";
import { TaskInputMediaPreviews } from "./TaskInputMediaPreviews";

interface TaskDetailModalProps {
  task: GenerationTask | undefined;
  open: boolean;
  onClose: () => void;
  onReusePrompt?: (prompt: string) => void;
}

function formatValue(value: unknown) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) return value.length ? value.join("、") : null;
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatTime(value?: string | null) {
  return value ? new Date(value).toLocaleString("zh-CN") : "-";
}

const inputFieldLabels: Record<string, string> = {
  negativePrompt: "反向提示词",
  imageFileIds: "参考图片",
  videoFileIds: "参考视频",
  audioFileIds: "参考音频",
  firstFrameImageFileId: "首帧图片",
  lastFrameImageFileId: "尾帧图片",
  outputImageCount: "出图数量",
  strength: "重绘强度",
  durationSeconds: "视频时长",
  aspectRatio: "画面比例",
  resolution: "清晰度",
  quality: "生成质量",
  size: "尺寸",
  outputFormat: "输出格式",
  seed: "随机种子",
  generateAudio: "生成音频",
  avatarTemplateId: "数字人模板",
  voiceId: "音色",
  speechRate: "语速",
};

const usageFieldLabels: Record<string, string> = {
  inputTokens: "输入 Tokens",
  outputTokens: "输出 Tokens",
  totalTokens: "总 Tokens",
  generatedImageCount: "生成图片数",
  generatedVideoSeconds: "生成视频时长（秒）",
  requestCount: "请求次数",
};

const inputMediaFieldKeys = new Set([
  "imageFileIds",
  "videoFileIds",
  "audioFileIds",
  "firstFrameImageFileId",
  "lastFrameImageFileId",
]);
const frameInputFieldKeys = new Set([
  "firstFrameImageFileId",
  "lastFrameImageFileId",
]);

function toFileIds(value: unknown) {
  const values = Array.isArray(value) ? value : [value];
  return [
    ...new Set(
      values.flatMap((item) => {
        const fileId = typeof item === "number" ? item : Number(item);
        return Number.isSafeInteger(fileId) && fileId > 0 ? [fileId] : [];
      }),
    ),
  ];
}

export function TaskDetailModal({
  task,
  open,
  onClose,
  onReusePrompt,
}: TaskDetailModalProps) {
  const prompt = task ? getTaskPrompt(task) : "";
  const preview = task ? getTaskPreviewArtifact(task) : undefined;
  const inputEntries = task
    ? Object.entries(task.input ?? {})
        .filter(([key]) => key !== "prompt" && key !== "speechText")
        .flatMap(([key, value]) => {
          const formatted = formatValue(value);
          if (!formatted) return [];
          const fileIds = inputMediaFieldKeys.has(key) ? toFileIds(value) : [];
          return [
            {
              key,
              label: inputFieldLabels[key] ?? key,
              value: formatted,
              ...(fileIds.length ? { fileIds } : {}),
            },
          ];
        })
    : [];
  const usageEntries = task?.usage
    ? Object.entries(task.usage).flatMap(([key, value]) => {
        const formatted = formatValue(value);
        return formatted
          ? [{ key, label: usageFieldLabels[key] ?? key, value: formatted }]
          : [];
      })
    : [];
  const frameInputEntries = inputEntries.filter((item) =>
    frameInputFieldKeys.has(item.key),
  );
  const regularInputEntries = inputEntries.filter(
    (item) => !frameInputFieldKeys.has(item.key),
  );

  return (
    <Modal
      className="task-detail-modal"
      width="calc(100vw - 64px)"
      open={open}
      footer={null}
      centered
      destroyOnHidden
      title={task ? `任务详情 · ${task.taskNo}` : "任务详情"}
      onCancel={onClose}
    >
      {task && (
        <div className="task-detail-layout">
          <section className="task-detail-preview">
            {preview?.previewUrl ? (
              preview.mediaType === "VIDEO" ? (
                <video controls playsInline src={preview.previewUrl} />
              ) : (
                <img src={preview.previewUrl} alt={prompt} />
              )
            ) : (
              <div
                className={`task-detail-placeholder${!isTerminalTaskStatus(task.status) ? " is-generating" : ""}`}
              >
                <span>{taskCapabilityLabels[task.capability]}</span>
                <b>{taskStatusLabels[task.status]}</b>
                {!isTerminalTaskStatus(task.status) && (
                  <Progress
                    percent={task.progress}
                    status={getTaskProgressStatus(task.status)}
                  />
                )}
              </div>
            )}
          </section>
          <aside className="task-detail-info">
            <header>
              <div>
                <Tag color={getTaskStatusColor(task.status)}>
                  {taskStatusLabels[task.status]}
                </Tag>
                <Tag>{taskCapabilityLabels[task.capability]}</Tag>
              </div>
              {!isTerminalTaskStatus(task.status) && (
                <Progress
                  percent={task.progress}
                  size="small"
                  status={getTaskProgressStatus(task.status)}
                />
              )}
            </header>
            <section className="task-detail-section">
              <h3>提示词</h3>
              <p>{prompt}</p>
              {onReusePrompt && (
                <Button onClick={() => onReusePrompt(prompt)}>
                  复用 Prompt
                </Button>
              )}
            </section>
            <section className="task-detail-meta">
              <span>
                <b>模式</b>
                {taskCapabilityLabels[task.capability]}
              </span>
              <span>
                <b>模型</b>
                {task.modelName ?? `模型 #${task.modelId}`}
              </span>
              <span>
                <b>任务号</b>
                {task.taskNo}
              </span>
              <span>
                <b>创建时间</b>
                {formatTime(task.createdAt)}
              </span>
              <span>
                <b>完成时间</b>
                {formatTime(task.completedAt)}
              </span>
              <span>
                <b>积分</b>
                {task.chargedPoints ?? task.quotedPoints ?? "-"}
              </span>
            </section>
            {inputEntries.length > 0 && (
              <section className="task-detail-section">
                <h3>输入素材与参数</h3>
                <div className="task-detail-kv">
                  {regularInputEntries.map((item) => (
                    <span
                      key={item.key}
                      className={item.fileIds ? "has-media" : undefined}
                    >
                      <b>{item.label}</b>
                      {item.fileIds ? (
                        <TaskInputMediaPreviews
                          key={`${task.taskNo}:${item.key}`}
                          mediaType={
                            item.key === "videoFileIds"
                              ? "VIDEO"
                              : item.key === "audioFileIds"
                                ? "AUDIO"
                                : "IMAGE"
                          }
                          fileIds={item.fileIds}
                          enabled={open}
                        />
                      ) : (
                        item.value
                      )}
                    </span>
                  ))}
                  {frameInputEntries.length > 0 && (
                    <div className="task-frame-input-row">
                      {frameInputEntries.map((item) => (
                        <span key={item.key}>
                          <b>{item.label}</b>
                          {item.fileIds ? (
                            <TaskInputMediaPreviews
                              key={`${task.taskNo}:${item.key}`}
                              mediaType={
                                item.key === "videoFileIds"
                                  ? "VIDEO"
                                  : item.key === "audioFileIds"
                                    ? "AUDIO"
                                    : "IMAGE"
                              }
                              fileIds={item.fileIds}
                              enabled={open}
                            />
                          ) : (
                            item.value
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}
            <section className="task-detail-section">
              <h3>生成信息</h3>
              <div className="task-detail-kv">
                <span>
                  <b>产物数量</b>
                  {task.output?.artifacts.length ?? 0}
                </span>
                {task.output?.text && (
                  <span>
                    <b>文本输出</b>
                    {task.output.text}
                  </span>
                )}
                {usageEntries.map((item) => (
                  <span key={item.key}>
                    <b>{item.label}</b>
                    {item.value}
                  </span>
                ))}
                {task.errorMessage && (
                  <span className="is-error">
                    <b>{task.errorCode ?? "错误信息"}</b>
                    {task.errorMessage}
                  </span>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}
    </Modal>
  );
}
